import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface MinutaPersonalFiltersValue {
  personalName: string;
  personalDocNum: string;
  employerName: string | null;
  categoryType: string | null;
  functionValue: string | null;
  eventType: string | null;
  requirementStatus: 'ALL' | 'CUMPLE' | 'NO_CUMPLE';
  photoChangeStatus: 'ALL' | 'YES' | 'NO';
}

export interface DialogFiltrosMinutaPersonalResult {
  filters: MinutaPersonalFiltersValue;
  summaryParts: string[];
}

export interface DialogFiltrosMinutaPersonalData {
  rows: any[];
  currentFilters?: Partial<MinutaPersonalFiltersValue>;
}

const DEFAULT_FILTERS: MinutaPersonalFiltersValue = {
  personalName: '',
  personalDocNum: '',
  employerName: null,
  categoryType: null,
  functionValue: null,
  eventType: null,
  requirementStatus: 'ALL',
  photoChangeStatus: 'ALL',
};

const EVENT_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'DIG', label: 'Registro con ID' },
  { value: 'QR', label: 'Registro con QR' },
  { value: 'FRON', label: 'Registro RF online' },
  { value: 'FROFF', label: 'Registro RF offline' },
];

const CATEGORY_LABELS: Record<string, string> = {
  ADMINISTRATIVO: 'Administrativo',
  FUNCIONARIO: 'Funcionario',
  CONTRATISTA: 'Contratista',
  OTRAS_PERSONAS: 'Otras personas',
  ASOCIADOS: 'Asociados',
  PROVEEDORES: 'Proveedores',
  VISITANTES: 'Visitantes',
};

@Component({
  selector: 'app-dialog-filtros-minuta-personal',
  templateUrl: './dialog-filtros-minuta-personal.component.html',
  styleUrls: ['./dialog-filtros-minuta-personal.component.scss']
})
export class DialogFiltrosMinutaPersonalComponent implements OnInit {
  form: FormGroup;

  employerOptions: string[] = [];
  categoryOptions: string[] = [];
  functionOptions: string[] = [];
  readonly eventTypeOptions = EVENT_TYPE_OPTIONS;

  constructor(
    private fb: FormBuilder,
    @Optional() public dialogRef: MatDialogRef<DialogFiltrosMinutaPersonalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: DialogFiltrosMinutaPersonalData
  ) { }

  ngOnInit(): void {
    const rows = this.data?.rows || [];
    this.employerOptions = this.uniqueSorted(rows.map(r => r.EmployerName));
    this.categoryOptions = this.uniqueSorted(rows.map(r => r.CategoryType));
    this.functionOptions = this.uniqueSorted(rows.map(r => r.Function));

    const current: MinutaPersonalFiltersValue = { ...DEFAULT_FILTERS, ...(this.data?.currentFilters || {}) };

    this.form = this.fb.group({
      personalName: [current.personalName],
      personalDocNum: [current.personalDocNum],
      employerName: [current.employerName],
      categoryType: [current.categoryType],
      functionValue: [current.functionValue],
      eventType: [current.eventType],
      requirementStatus: [current.requirementStatus],
      photoChangeStatus: [current.photoChangeStatus],
    });
  }

  private uniqueSorted(values: Array<string | null | undefined>): string[] {
    return Array.from(new Set(values.filter(v => !!v))).sort((a, b) => a.localeCompare(b));
  }

  categoryLabel(value: string): string {
    return CATEGORY_LABELS[value] || value;
  }

  get activeCount(): number {
    if (!this.form) { return 0; }
    return this.buildSummaryParts(this.form.value).length;
  }

  private buildSummaryParts(value: MinutaPersonalFiltersValue): string[] {
    const summaryParts: string[] = [];

    if (value.personalName?.trim()) { summaryParts.push(`Nombre: ${value.personalName.trim()}`); }
    if (value.personalDocNum?.trim()) { summaryParts.push(`Documento: ${value.personalDocNum.trim()}`); }
    if (value.employerName) { summaryParts.push(`Empresa: ${value.employerName}`); }
    if (value.categoryType) { summaryParts.push(`Categoría: ${this.categoryLabel(value.categoryType)}`); }
    if (value.functionValue) { summaryParts.push(`Función: ${value.functionValue}`); }
    if (value.eventType) {
      const found = this.eventTypeOptions.find(o => o.value === value.eventType);
      summaryParts.push(`Tipo de registro: ${found ? found.label : value.eventType}`);
    }
    if (value.requirementStatus === 'CUMPLE') { summaryParts.push('Requisitos: cumplen todos'); }
    if (value.requirementStatus === 'NO_CUMPLE') { summaryParts.push('Requisitos: no cumplen'); }
    if (value.photoChangeStatus === 'YES') { summaryParts.push('Cambio de foto: solicitado'); }
    if (value.photoChangeStatus === 'NO') { summaryParts.push('Cambio de foto: no solicitado'); }

    return summaryParts;
  }

  clear(): void {
    this.form.reset({ ...DEFAULT_FILTERS });
  }

  apply(): void {
    const value: MinutaPersonalFiltersValue = this.form.value;
    const result: DialogFiltrosMinutaPersonalResult = {
      filters: value,
      summaryParts: this.buildSummaryParts(value)
    };
    this.dialogRef?.close(result);
  }

  close(): void {
    this.dialogRef?.close();
  }
}
